/**
 * Alpine components for the mod support surfaces (#3457):
 *
 * - modSupportToolbar: the pill-toolbar filter cluster used by the support
 *   inbox and the flat Messages list. Follows the appeals-toolbar convention
 *   (a single `active` panel key with dropdown panels at every width) and
 *   embeds the shared date-range-picker mixin for the created-at window.
 *
 * - modTranslateMessage: per-row translation of a foreign-language message
 *   body into English via the same public Google endpoint the mobile app's
 *   message translate uses. Client-side only; internal mod surface.
 */

import { createDatePickerMixin } from '../modules/trip-date-picker.js';

/**
 * @param {Object} config
 * @param {string} config.baseUrl - The page URL filters navigate to
 * @param {Object} config.params - Currently-applied query params (offset excluded)
 * @param {string} [config.dateFrom] - Applied from-date (YYYY-MM-DD)
 * @param {string} [config.dateTo] - Applied to-date (YYYY-MM-DD)
 */
export function modSupportToolbar(config) {
    return {
        // History filters need past dates; future dates cannot match anything.
        ...createDatePickerMixin({ allowPast: true, disableFuture: true }),

        active: null,
        baseUrl: config.baseUrl,
        params: config.params || {},
        startDate: config.dateFrom || '',
        endDate: config.dateTo || '',

        toggle(panel) {
            this.active = this.active === panel ? null : panel;
        },

        close() {
            this.active = null;
        },

        /**
         * The date-range-picker include binds its visibility to this proxy.
         * The mixin's selectDate also writes `showDatePicker = false` after an
         * end-date pick; that property is unused here, so the panel stays open
         * until Apply by design.
         */
        get datesOpen() {
            return this.active === 'dates';
        },
        set datesOpen(value) {
            this.active = value ? 'dates' : null;
        },

        /**
         * Navigate to the page with the current params plus overrides. A null
         * override removes the param; offset always resets (a changed filter
         * starts from page one).
         */
        applyWith(overrides) {
            const merged = { ...this.params, ...overrides };
            const query = new URLSearchParams();
            Object.keys(merged).forEach((key) => {
                const value = merged[key];
                if (value !== null && value !== undefined && value !== '') {
                    query.set(key, value);
                }
            });
            query.delete('offset');
            const qs = query.toString();
            window.location.href = qs ? `${this.baseUrl}?${qs}` : this.baseUrl;
        },

        applyDates() {
            this.close();
            this.applyWith({ dateFrom: this.startDate || null, dateTo: this.endDate || null });
        },

        clearDates() {
            this.startDate = '';
            this.endDate = '';
            this.datePickerStep = 'start';
        },

        /**
         * The dates pill label for the APPLIED window (the params in the URL,
         * not the in-panel selection): "Jul 1 – Jul 9", one-sided variants, or
         * the placeholder.
         */
        get dateLabel() {
            const from = this.params.dateFrom ? this.formatDateShort(this.params.dateFrom) : '';
            const to = this.params.dateTo ? this.formatDateShort(this.params.dateTo) : '';
            if (from && to) return `${from} – ${to}`;
            if (from) return `From ${from}`;
            if (to) return `Until ${to}`;
            return 'Dates';
        },
    };
}

/** Response segments live at [0][i][0]; concatenating them re-forms the text. */
const TRANSLATE_ENDPOINT = 'https://translate.googleapis.com/translate_a/single';

/** Bodies past this length are truncated before translation (URL size bound). */
const TRANSLATE_MAX_CHARS = 4000;

export function modTranslateMessage() {
    return {
        translation: '',
        translating: false,
        translateError: false,
        translationShown: false,
        truncated: false,

        async translate() {
            if (this.translation) {
                this.translationShown = !this.translationShown;
                return;
            }
            const source = this.$refs.messageBody?.textContent?.trim();
            if (!source || this.translating) {
                return;
            }

            this.translating = true;
            this.translateError = false;
            try {
                let text = source;
                if (text.length > TRANSLATE_MAX_CHARS) {
                    text = text.slice(0, TRANSLATE_MAX_CHARS);
                    this.truncated = true;
                }
                const params = new URLSearchParams({
                    client: 'gtx',
                    sl: 'auto',
                    tl: 'en',
                    dt: 't',
                    q: text,
                });
                const response = await fetch(`${TRANSLATE_ENDPOINT}?${params}`);
                if (!response.ok) {
                    throw new Error(`translate http ${response.status}`);
                }
                const data = await response.json();
                const segments = Array.isArray(data?.[0]) ? data[0] : [];
                this.translation = segments.map((seg) => seg?.[0] || '').join('');
                if (!this.translation) {
                    throw new Error('empty translation');
                }
                this.translationShown = true;
            } catch (e) {
                this.translateError = true;
            } finally {
                this.translating = false;
            }
        },
    };
}
